import Config from '@/shared/Config'

/**
 * Feature flags resolved from 3 levels (lowest to highest priority):
 *   1. Build-time:    VUE_APP_FF_<FLAG_NAME>=true
 *   2. Organization:  organization.feature_flags from /organizations/{id}/info
 *   3. Project:       project.feature_flags from /projects/{id}/info
 *
 * A flag none of those three mention falls back to `defaultFlagValue()`.
 */

let resolved = null

/**
 * Fallback for a flag that no level above resolved.
 *
 * ON under the local dev server, so a feature in development is reachable
 * without having to add a `VUE_APP_FF_*` entry for it — there is no Docker
 * build locally to supply one. OFF everywhere else.
 *
 * Only `vue-cli-service serve` (`yarn start`) sets NODE_ENV=development;
 * every image runs `yarn build` with NODE_ENV=production, so builds are
 * unaffected and a flag stays OFF unless it is explicitly enabled.
 *
 * This is only a *fallback*: any explicit value still wins, so
 * `VUE_APP_FF_<FLAG>=false` in `.env.development.local` is how you exercise
 * a flag-off path locally.
 */
export function defaultFlagValue () {
  return process.env.NODE_ENV === 'development'
}

function buildTimeFlags () {
  const prefix = 'VUE_APP_FF_'
  const flags = {}
  for (const [key, value] of Object.entries(process.env)) {
    if (key.startsWith(prefix)) {
      const name = key.slice(prefix.length).toLowerCase()
      flags[name] = value === 'true' || value === '1'
    }
  }
  return flags
}

export async function resolveFlags () {
  const config = await Config()
  resolved = {
    ...buildTimeFlags(),
    ...(config.ORG_FEATURE_FLAGS || {}),
    ...(config.PROJECT_FEATURE_FLAGS || {})
  }
  return resolved
}

export function getFlags () {
  return resolved || {}
}

export function hasFlag (name) {
  return getFlags()[name] === true
}

export function getFlagValue (name, defaultValue = null) {
  const flags = getFlags()
  return name in flags ? flags[name] : defaultValue
}
